import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Activity, Target, Plus } from 'lucide-react';

interface WorkoutTemplatesProps {
  searchQuery: string;
  filter: string;
}

export function WorkoutTemplates({ searchQuery, filter }: WorkoutTemplatesProps) {
  const templates = [
    {
      id: 1,
      name: 'Full Body Strength',
      type: 'strength',
      duration: 60,
      intensity: 'High',
      targetStrain: 14.5,
      image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&q=80'
    },
    {
      id: 2,
      name: 'HIIT Circuit',
      type: 'hiit',
      duration: 45,
      intensity: 'High',
      targetStrain: 15.2,
      image: 'https://images.unsplash.com/photo-1517838277536-f5f99be501cd?w=800&q=80'
    },
    {
      id: 3,
      name: 'Recovery Flow',
      type: 'recovery',
      duration: 30,
      intensity: 'Low',
      targetStrain: 6.5,
      image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&q=80'
    }
  ];

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' || template.type === filter;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {filteredTemplates.map((template, index) => (
        <motion.div
          key={template.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="theme-card group relative overflow-hidden"
        >
          {/* Thumbnail */}
          <div className="relative h-40 overflow-hidden">
            <img
              src={template.image}
              alt={template.name}
              className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent opacity-60" />
            
            {/* Play Button Overlay */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <button className="theme-button w-10 h-10 rounded-full flex items-center justify-center">
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-4 space-y-3">
            <div>
              <h3 className="font-medium">{template.name}</h3>
              <p className="text-sm text-muted-foreground">{template.type}</p>
            </div>

            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {template.duration}m
              </div>
              <div className="flex items-center gap-1">
                <Activity className="w-4 h-4" />
                {template.intensity}
              </div>
              <div className="flex items-center gap-1">
                <Target className="w-4 h-4" />
                {template.targetStrain}
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}